import React from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { displayDialog } from "store/actions/utilActions";
import { invoiceUpdated } from "store/actions/invoiceActions";
//#endregion
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { FormHandles } from "models/propTypes/genericFormProps";
import { InvoiceFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import {
  GenericCommentsView,
  StyledButton,
  TitledContainerInner,
  EntityCardContent,
  Profiles,
} from "components/common";
import {
  InvoiceFooter,
  useInvoiceFileViewer,
  useInvoicePreview,
  useInvoiceSnoozeAction,
  useSendToDebtCollectionDialog,
} from "components/invoice";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { useInvoiceService, useDebtCollectionService } from "serviceHooks";
//#endregion
import { InvoiceHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoiceTodoOverdue: React.FC<TodoCardProps> = (props) => {
  const debtCollectionService = useDebtCollectionService();
  const invoiceService = useInvoiceService();

  const { onActionSuccess } = props;
  const invoice = props.item.invoice;

  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const formRef = React.useRef<FormHandles>(null);

  const [loading, setLoading] = React.useState(false);

  const snoozeAction = useInvoiceSnoozeAction(invoice!.id, onActionSuccess);

  const { fileViewerComponent } = useInvoiceFileViewer(invoice!);
  const loggedInUserId = useSelector((state: AppState) => state.auth.loggedInUser.personId);

  const onUpdateInvoice = () => {
    return (formRef.current && formRef.current.submit()) || new Promise<void>((resolve) => resolve());
  };

  const formToAPIHandler = async (): Promise<WintSuperkollAPIModel.InvoicePut | undefined> => {
    const isValid = formRef.current?.validate
      ? await formRef.current.validate(TranslationKeys.Validations.FormContainsErrors)
      : false;

    if (isValid) {
      const values: InvoiceFormTypes.Todo = formRef.current?.getValues && formRef.current.getValues();

      if (values && invoice) {
        return invoiceService.convertTodoFormToAPIRequest(values, invoice);
      } else {
        return undefined;
      }
    }
  };

  const reloadTodo = () => {
    if (invoice) {
      setLoading(true);
      invoiceService
        .get(invoice.id)
        .then((result) => {
          if (
            loggedInUserId === result.ownerId &&
            (result.status === WintSuperkollAPIModel.InvoiceStatus.NotSent ||
              result.status === WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent)
          ) {
            dispatch(invoiceUpdated(result));
          }
        })
        .finally(() => {
          setLoading(false);
          onActionSuccess();
        });
    }
  };

  const onViewAllButtonClick = () => {
    navigate(UrlHelper.formatPath(`/Invoice/${invoice?.id}`), {
      state: { background: location },
    });
  };

  const getDebtCollectionForm = useSendToDebtCollectionDialog(onActionSuccess);
  const { showPreviewDialog } = useInvoicePreview();
  const onShowInvoicePreview = async () => {
    showPreviewDialog(invoice, undefined, undefined, onActionSuccess, undefined, true);
  };

  const onSendInvoiceClaimClicked = () => {
    if (invoice) dispatch(displayDialog(getDebtCollectionForm(invoice.id)));
  };

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={LocalizationHelperInstance.getFormattedCurrency(
            invoice?.leftToPay ?? 0,
            invoice?.currency
          )}
          descriptionHeadingTwo={
            invoice?.customerName ? (
              <Profiles.CustomerProfileLink
                id={invoice.customerId}
                name={invoice.customerName ?? ""}
                className={cssExports.titleLink}
              />
            ) : (
              LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.NoName)
            )
          }
          descriptionheadingThree={
            invoice?.serialNumber !== undefined
              ? `${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Invoice.InvoiceNumber
                )}: ${InvoiceHelper.formatInvoiceNumber(invoice!.serialNumber)}`
              : ""
          }
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.Due_Date
          )}: ${LocalizationHelperInstance.getFormattedDate(invoice!.dueDate)}`}
          onViewAllClick={onViewAllButtonClick}
          quickActions={[snoozeAction]}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReminderHeader)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReminderSubHeader)}
        >
          <Grid container marginTop={3}>
            <Grid container spacing={2}>
              <Grid item>
                <StyledButton size="sm" onClick={onShowInvoicePreview}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendReminder)}
                </StyledButton>
              </Grid>
              {debtCollectionService.canAllowDebtCollection(invoice?.reminderInvoicesCount) ? (
                <Grid item>
                  <StyledButton size="sm" onClick={onSendInvoiceClaimClicked} secondary>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendInvoiceClaim)}
                  </StyledButton>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </TitledContainerInner>
      }
      infoViewer={fileViewerComponent}
      footer={
        invoice ? (
          <InvoiceFooter
            invoice={invoice}
            formToAPIHandler={formToAPIHandler}
            createOrUpdateInvoice={onUpdateInvoice}
            reloadHandler={reloadTodo}
            markAsDoneHandler={onActionSuccess}
            hideSave
            disableSuccessMessages
            setLoading={setLoading}
          />
        ) : (
          <></>
        )
      }
      loading={loading}
    >
      <GenericCommentsView parentId={invoice!.id} type={WintSuperkollAPIModel.GenericCommentType.Invoice} />
    </EntityCardContent>
  );
};

export default InvoiceTodoOverdue;
